import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, Mail, Monitor, Clock, TestTube, Check, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import UnifiedMenu from '../components/UnifiedMenu';

const FREQUENCIES = [
  { value: 'daily', label: 'Once a Day' },
  { value: 'twice_daily', label: 'Twice a Day' },
  { value: 'weekly', label: 'Once a Week' },
];

const TIMES = [
  '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '14:00', '16:00', '18:00', '20:00'
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (US)' },
  { value: 'America/Chicago', label: 'Central (US)' },
  { value: 'America/Denver', label: 'Mountain (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific (US)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

export default function NotificationSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [browserPermission, setBrowserPermission] = useState('default');
  const [prefs, setPrefs] = useState({
    enabled: true,
    channels: { browser: true, email: true },
    frequency: 'twice_daily',
    reminder_time: '08:30',
    reminder_times: ['08:30', '15:30'],
    timezone: 'UTC',
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
  });

  useEffect(() => {
    loadPreferences();
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`);
      setPrefs(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/notifications/preferences`, prefs);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const requestBrowserPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser notifications not supported');
      return;
    }
    const permission = await Notification.requestPermission();
    setBrowserPermission(permission);
    if (permission === 'granted') {
      setPrefs(p => ({ ...p, channels: { ...p.channels, browser: true } }));
      toast.success('Browser notifications enabled');
      // Show a test notification
      new Notification('Mind My Mojo', {
        body: 'You will now receive emotional check-in reminders here.',
        icon: '/favicon.ico'
      });
    } else {
      toast.error('Notification permission denied');
    }
  };

  const sendTestNotification = async (channel) => {
    setTesting(channel);
    try {
      if (channel === 'browser') {
        if (Notification.permission === 'granted') {
          new Notification('Mind My Mojo Check-in', {
            body: 'How are you feeling right now? Take a moment to check in with your emotions.',
            icon: '/favicon.ico'
          });
          toast.success('Browser notification sent');
        } else {
          toast.error('Please enable browser notifications first');
        }
      } else if (channel === 'email') {
        const res = await axios.post(`${API}/notifications/test`, { channel: 'email' });
        if (res.data.success) {
          toast.success('Test email sent! Check your inbox.');
        } else {
          toast.error(res.data.error || 'Failed to send test email');
        }
      }
    } catch (err) {
      toast.error('Failed to send test notification');
    } finally {
      setTesting(null);
    }
  };

  const updatePref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  };

  const toggleChannel = (channel) => {
    setPrefs(prev => ({
      ...prev,
      channels: { ...prev.channels, [channel]: !prev.channels[channel] }
    }));
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-b from-white via-stone-50 to-stone-100">
        <Loader className="w-8 h-8 animate-spin text-hunter-green" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <UnifiedMenu active="notifications" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />
      
      {/* Header */}
      <div className="flex-none px-4 pt-4 pb-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-stone-100" data-testid="notif-back-btn">
            <ArrowLeft className="w-5 h-5 text-warm-charcoal" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
              Notifications
            </h1>
            <p className="text-xs text-warm-charcoal/50">Manage your check-in reminders</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="max-w-lg mx-auto space-y-4 mt-2">
          
          {/* Master Toggle */}
          <div className="zen-card p-4 flex items-center justify-between" data-testid="notif-master-toggle">
            <div className="flex items-center gap-3">
              {prefs.enabled ? (
                <Bell className="w-5 h-5 text-hunter-green" />
              ) : (
                <BellOff className="w-5 h-5 text-warm-charcoal/40" />
              )}
              <div>
                <p className="font-semibold text-sm text-warm-charcoal">Check-in Reminders</p>
                <p className="text-xs text-warm-charcoal/50">Get reminded to track your emotions</p>
              </div>
            </div>
            <button
              onClick={() => updatePref('enabled', !prefs.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${prefs.enabled ? 'bg-hunter-green' : 'bg-stone-300'}`}
              data-testid="notif-enable-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${prefs.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {prefs.enabled && (
            <>
              {/* Channels */}
              <div className="zen-card p-4 space-y-3" data-testid="notif-channels">
                <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider">Notification Channels</h3>
                
                {/* Browser */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-4 h-4 text-warm-charcoal/60" />
                    <div>
                      <p className="text-sm font-medium text-warm-charcoal">Browser Notifications</p>
                      <p className="text-xs text-warm-charcoal/40">
                        {browserPermission === 'granted' ? 'Enabled' : browserPermission === 'denied' ? 'Blocked in browser settings' : 'Not yet enabled'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {browserPermission !== 'granted' && (
                      <button
                        onClick={requestBrowserPermission}
                        className="text-xs px-3 py-1 rounded-full bg-hunter-green/10 text-hunter-green font-medium hover:bg-hunter-green/20"
                        data-testid="notif-browser-enable"
                      >
                        Enable
                      </button>
                    )}
                    {browserPermission === 'granted' && (
                      <>
                        <button
                          onClick={() => sendTestNotification('browser')}
                          disabled={testing === 'browser'}
                          className="text-xs px-2 py-1 rounded-full bg-stone-100 text-warm-charcoal/60 hover:bg-stone-200"
                          data-testid="notif-browser-test"
                        >
                          {testing === 'browser' ? <Loader className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}
                        </button>
                        <button
                          onClick={() => toggleChannel('browser')}
                          className={`relative w-10 h-5 rounded-full transition-colors ${prefs.channels.browser ? 'bg-hunter-green' : 'bg-stone-300'}`}
                          data-testid="notif-browser-toggle"
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${prefs.channels.browser ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center justify-between py-2 border-t border-stone-100">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-warm-charcoal/60" />
                    <div>
                      <p className="text-sm font-medium text-warm-charcoal">Email Reminders</p>
                      <p className="text-xs text-warm-charcoal/40">{user?.email || 'Your email'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => sendTestNotification('email')}
                      disabled={testing === 'email'}
                      className="text-xs px-2 py-1 rounded-full bg-stone-100 text-warm-charcoal/60 hover:bg-stone-200"
                      data-testid="notif-email-test"
                    >
                      {testing === 'email' ? <Loader className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => toggleChannel('email')}
                      className={`relative w-10 h-5 rounded-full transition-colors ${prefs.channels.email ? 'bg-hunter-green' : 'bg-stone-300'}`}
                      data-testid="notif-email-toggle"
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${prefs.channels.email ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="zen-card p-4 space-y-4" data-testid="notif-schedule">
                <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider">Schedule</h3>
                
                {/* Frequency */}
                <div>
                  <label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">How often?</label>
                  <div className="grid grid-cols-3 gap-2">
                    {FREQUENCIES.map(f => (
                      <button
                        key={f.value}
                        onClick={() => updatePref('frequency', f.value)}
                        className={`py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                          prefs.frequency === f.value
                            ? 'bg-hunter-green text-white shadow-md'
                            : 'bg-stone-100 text-warm-charcoal/60 hover:bg-stone-200'
                        }`}
                        data-testid={`notif-freq-${f.value}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time */}
                <div>
                  <label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Reminder time
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TIMES.map(t => (
                      <button
                        key={t}
                        onClick={() => updatePref('reminder_time', t)}
                        className={`py-1.5 px-3 rounded-lg text-xs font-medium transition-all ${
                          prefs.reminder_time === t
                            ? 'bg-hunter-green text-white'
                            : 'bg-stone-100 text-warm-charcoal/60 hover:bg-stone-200'
                        }`}
                        data-testid={`notif-time-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timezone */}
                <div>
                  <label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">Timezone</label>
                  <select
                    value={prefs.timezone}
                    onChange={(e) => updatePref('timezone', e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-stone-200 text-sm bg-white focus:border-hunter-green focus:ring-2 focus:ring-hunter-green/20 outline-none"
                    data-testid="notif-timezone"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Quiet Hours */}
              <div className="zen-card p-4 space-y-3" data-testid="notif-quiet-hours">
                <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider">Quiet Hours</h3>
                <p className="text-xs text-warm-charcoal/40">No notifications during these hours</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-warm-charcoal/50 mb-1 block">From</label>
                    <select
                      value={prefs.quiet_hours_start}
                      onChange={(e) => updatePref('quiet_hours_start', e.target.value)}
                      className="w-full p-2 rounded-lg border border-stone-200 text-sm bg-white"
                      data-testid="notif-quiet-start"
                    >
                      {Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-warm-charcoal/30 mt-4">to</span>
                  <div className="flex-1">
                    <label className="text-xs text-warm-charcoal/50 mb-1 block">Until</label>
                    <select
                      value={prefs.quiet_hours_end}
                      onChange={(e) => updatePref('quiet_hours_end', e.target.value)}
                      className="w-full p-2 rounded-lg border border-stone-200 text-sm bg-white"
                      data-testid="notif-quiet-end"
                    >
                      {Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Save Button */}
          <Button
            onClick={savePreferences}
            disabled={saving}
            className="w-full zen-button bg-hunter-green text-white hover:bg-hunter-green/90 h-11"
            data-testid="notif-save-btn"
          >
            {saving ? (
              <Loader className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
}
